import * as React from "react"

import {
  BarChart,
  Build,
  People,
  FormatListBulleted,
  Assignment,
  SmartToy,
  Settings,
  ExitToApp,
  ExpandLess,
  ExpandMore,
  Timeline,
  ViewModule,
  Cloud,
  ReportProblem,
  QuestionAnswer,
} from '@mui/icons-material'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Button,
  Collapse,
  Avatar,
} from '@mui/material'
import { Link, useNavigate, useLocation } from "react-router-dom"

import { authService } from "../api/services/auth"
import { isAdminRole } from "../lib/user-utils"

import type { User, UserRole } from "../api/types"

const getNavItems = (userRole: UserRole) => {
  const isAdmin = isAdminRole(userRole)
  const isSupport = userRole === 'support'

  if (isSupport) {
    return [
      {
        title: "Analytics",
        icon: <BarChart />,
        url: "/analytics",
        items: []
      },
{
        title: "Unhealthy",
        icon: <ReportProblem />,
        url: "/unhealthy-customers",
        items: []
      },
      {
        title: "Users",
        icon: <People />,
        items: [
          { title: 'Regular Users', url: '/regular-users' },
          { title: 'Content-Only Users', url: '/content-only-users' },
        ]
      },
      {
        title: "Orders",
        icon: <Assignment />,
        items: [
          { title: 'All Orders', url: '/orders' },
        ]
      },
      {
        title: "Event Logs",
        icon: <Timeline />,
        url: "/event-logs",
        items: []
      },
      {
        title: "GCP Logs",
        icon: <Cloud />,
        url: "/gcp-logs",
        items: []
      },
    ]
  }

  return [
    {
      title: "Analytics",
      icon: <BarChart />,
      url: "/analytics",
      items: []
    },
    {
      title: "Unhealthy",
      icon: <ReportProblem />,
      url: "/unhealthy-customers",
      items: []
    },
    ...((isAdmin || isSupport || userRole === 'marketing' || userRole === 'doctor') ? [{
      title: "Tools",
      icon: <Build />,
      url: "/tools",
      items: []
    }] : []),
    {
      title: "Users",
      icon: <People />,
      items: [
        { title: 'Regular Users', url: '/regular-users' },
        { title: 'Content-Only Users', url: '/content-only-users' },
        { title: 'Doctors', url: '/doctors' },
        { title: 'Support Users', url: '/support-users' },
        { title: 'Admin Users', url: '/admin-users' },
        { title: 'Marketing Users', url: '/marketing-users' },
      ]
    },
    {
      title: "Orders",
      icon: <Assignment />,
      items: [
        { title: 'All Orders', url: '/orders' },
      ]
    },
    ...((isAdmin || userRole === 'doctor') ? [{
      title: "AI Reports",
      icon: <SmartToy />,
      items: [
        { title: 'Pending Review', url: '/ai-qc/pending' },
        { title: 'Reviewed', url: '/ai-qc/reviewed' },
      ]
    }] : []),
    ...((isAdmin || userRole === 'doctor') ? [{
      title: "Prompts",
      icon: <FormatListBulleted />,
      url: "/prompts",
      items: []
    }] : []),
    {
      title: "Package Mappings",
      icon: <ViewModule />,
      url: "/package-mappings",
      items: []
    },
    ...((isAdmin || isSupport) ? [{
      title: "Event Logs",
      icon: <Timeline />,
      url: "/event-logs",
      items: []
    }] : []),
    ...((isAdmin || isSupport) ? [{
      title: "GCP Logs",
      icon: <Cloud />,
      url: "/gcp-logs",
      items: []
    }] : []),
    ...((isAdmin || userRole === 'marketing') ? [{
      title: "Custdev interviews",
      icon: <QuestionAnswer />,
      url: "/custdev",
      items: []
    }] : []),
    ...(isAdmin ? [{
      title: "Settings",
      icon: <Settings />,
      url: "/settings",
      items: []
    }] : []),
  ]
}

const drawerWidth = 240

interface AppSidebarProps {
  open?: boolean
  onClose?: () => void
  variant?: 'permanent' | 'temporary'
  user?: User | null
}

export function AppSidebar({
  open = true,
  onClose,
  variant = 'permanent',
  user
}: AppSidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [expandedItems, setExpandedItems] = React.useState<string[]>(['Users', 'Orders', 'AI Reports'])

  const navItems = React.useMemo(() => {
    if (!user) return []
    return getNavItems(user.role)
  }, [user])

  const handleLogout = async () => {
    try {
      await authService.logout()
      navigate("/login")
    } catch {
      // Even if logout fails, redirect to login
      navigate("/login")
    }
  }

  const handleExpandClick = (title: string) => {
    setExpandedItems(prev =>
      prev.includes(title)
        ? prev.filter(item => item !== title)
        : [...prev, title]
    )
  }

  const isActive = (url: string) => location.pathname === url
  const role = user?.role || ''

  const activeStyles = {
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    borderRadius: 1.5,
    '&:hover': { bgcolor: 'primary.dark' },
    '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
  }

  const itemStyles = {
    mx: 1,
    px: 1.5,
    py: 0.5,
    borderRadius: 1.5,
    '&:hover': { bgcolor: 'action.hover' },
  }

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
          <Box
            component="img"
            src="/logo.jpeg"
            alt="Aura Logo"
            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        </Avatar>
        <Box>
          <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
            AuraCare
          </Typography>
          <Typography variant="caption" color="text.secondary" fontSize={11}>
            {role.charAt(0).toUpperCase() + role.slice(1)}
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Navigation */}
      <List sx={{ flex: 1, py: 0.5, overflow: 'auto' }}>
        {navItems.map((item) => {
          const isItemActive = item.url ? isActive(item.url) : false
          return (
            <React.Fragment key={item.title}>
              <ListItem disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  onClick={() => {
                    if (item.items && item.items.length > 0) {
                      handleExpandClick(item.title)
                    } else if (item.url) {
                      navigate(item.url)
                      if (variant === 'temporary') onClose?.()
                    }
                  }}
                  sx={{
                    ...itemStyles,
                    ...(isItemActive ? activeStyles : {}),
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: isItemActive ? 'inherit' : 'text.secondary' }}>
                    {React.cloneElement(item.icon, { fontSize: 'small' })}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{ variant: 'body2', fontSize: 13, fontWeight: 500 }}
                  />
                  {item.items && item.items.length > 0 && (
                    expandedItems.includes(item.title)
                      ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} />
                      : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />
                  )}
                </ListItemButton>
              </ListItem>

              {item.items && item.items.length > 0 && (
                <Collapse in={expandedItems.includes(item.title)} timeout="auto">
                  <List component="div" disablePadding>
                    {item.items.map((subItem) => {
                      const isSubActive = isActive(subItem.url)
                      return (
                        <ListItem key={subItem.title} disablePadding>
                          <ListItemButton
                            component={Link}
                            to={subItem.url}
                            onClick={() => {
                              if (variant === 'temporary') onClose?.()
                            }}
                            sx={{
                              ...itemStyles,
                              pl: 5.5,
                              py: 0.35,
                              ...(isSubActive ? activeStyles : {}),
                            }}
                          >
                            <ListItemText
                              primary={subItem.title}
                              primaryTypographyProps={{ variant: 'body2', fontSize: 12.5, fontWeight: isSubActive ? 500 : 400 }}
                            />
                          </ListItemButton>
                        </ListItem>
                      )
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          )
        })}
      </List>

      <Divider />

      {/* Footer */}
      <Box sx={{ p: 1.5 }}>
        <Button
          fullWidth
          size="small"
          variant="text"
          startIcon={<ExitToApp sx={{ fontSize: 18 }} />}
          onClick={handleLogout}
          sx={{ justifyContent: 'flex-start', fontSize: 13, textTransform: 'none', color: 'text.secondary' }}
        >
          Sign Out
        </Button>
      </Box>
    </Box>
  )

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          bgcolor: 'background.default',
          transition: (theme) => theme.transitions.create('transform', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(variant === 'permanent' && !open && {
            transform: `translateX(-${drawerWidth}px)`,
          }),
        },
      }}
    >
      {drawerContent}
    </Drawer>
  )
}
